import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { PlatformIcon, PLATFORM_CONFIG } from "@/components/shared/SocialLinksSection";
import { Plus, Trash2, CheckCircle, XCircle, Loader2, Search } from "lucide-react";

const PLATFORMS = [
  "instagram", "facebook", "tiktok", "twitter", "youtube", "pinterest",
  "yelp_page", "google_maps", "whatsapp", "snapchat", "threads",
  "linkedin", "twitch", "reddit", "discord", "clubhouse",
];

export default function SocialMediaPanel() {
  const [businesses, setBusinesses] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [links, setLinks] = useState([]);
  const [newPlatform, setNewPlatform] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  useEffect(() => {
    loadBusinesses();
    loadPending();
  }, []);

  const loadBusinesses = async () => {
    try {
      const res = await axios.get(`${API}/admin/businesses`);
      setBusinesses(res.data || []);
    } catch { /* ignore */ }
  };

  const loadPending = async () => {
    setLoadingPending(true);
    try {
      const res = await axios.get(`${API}/admin/social-media/pending`);
      setPending(res.data.pending || []);
    } catch { /* ignore */ }
    finally { setLoadingPending(false); }
  };

  const selectBusiness = async (biz) => {
    setSelectedBiz(biz);
    try {
      const res = await axios.get(`${API}/admin/business/${biz.business_id}/social-media`);
      setLinks(res.data.social_media || []);
    } catch { setLinks([]); }
  };

  const addLink = async () => {
    if (!newPlatform || !newUrl.trim()) { toast.error("Select platform and enter URL"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/business/${selectedBiz.business_id}/social-media`, { platform: newPlatform, url: newUrl.trim() });
      toast.success(`Added ${newPlatform} link`);
      setNewPlatform("");
      setNewUrl("");
      selectBusiness(selectedBiz);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
    finally { setAdding(false); }
  };

  const removeLink = async (platform, url) => {
    try {
      await axios.delete(`${API}/admin/business/${selectedBiz.business_id}/social-media`, { data: { platform, url } });
      toast.success("Removed");
      selectBusiness(selectedBiz);
    } catch { toast.error("Failed to remove"); }
  };

  const approvePending = async (item) => {
    try {
      await axios.post(`${API}/admin/social-media/approve`, { business_id: item.business_id, platform: item.platform, url: item.url });
      toast.success("Approved");
      loadPending();
      if (selectedBiz?.business_id === item.business_id) selectBusiness(selectedBiz);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rejectPending = async (item) => {
    try {
      await axios.post(`${API}/admin/social-media/reject`, { business_id: item.business_id, platform: item.platform, url: item.url });
      toast.success("Rejected");
      loadPending();
    } catch { toast.error("Failed"); }
  };

  const filtered = businesses.filter(b => b.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="social-media-panel">
      {/* Moderation Queue */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-2">User-Submitted Links ({pending.length} pending)</h3>
        {loadingPending ? (
          <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
        ) : pending.length === 0 ? (
          <p className="text-xs text-slate-400">No pending submissions</p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="pending-social-list">
            {pending.map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`pending-item-${i}`}>
                <PlatformIcon platform={item.platform} size={16} />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-slate-700 block">{item.business_name}</span>
                  <span className="text-[10px] text-slate-400 block truncate">{PLATFORM_CONFIG[item.platform]?.label || item.platform}: {item.url}</span>
                  <span className="text-[10px] text-slate-400">by {item.submitted_by_name || "user"}</span>
                </div>
                <button onClick={() => approvePending(item)} className="p-1.5 rounded-lg bg-green-100 hover:bg-green-200 text-green-600" data-testid={`approve-${i}`}>
                  <CheckCircle className="w-4 h-4" />
                </button>
                <button onClick={() => rejectPending(item)} className="p-1.5 rounded-lg bg-red-100 hover:bg-red-200 text-red-600" data-testid={`reject-${i}`}>
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Business Selector */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-2">Manage Business Social Links</h3>
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search businesses..."
            className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-300"
            data-testid="social-biz-search"
          />
        </div>
        {search && (
          <div className="max-h-32 overflow-y-auto bg-white border border-slate-200 rounded-lg mb-3">
            {filtered.slice(0, 20).map((b) => (
              <button
                key={b.business_id}
                onClick={() => { selectBusiness(b); setSearch(""); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-0 ${selectedBiz?.business_id === b.business_id ? "bg-indigo-50 font-semibold" : ""}`}
                data-testid={`biz-option-${b.business_id}`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Business: Social Links */}
      {selectedBiz && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="selected-biz-social">
          <h4 className="text-sm font-bold text-slate-700 mb-3">{selectedBiz.name} — Social Media</h4>

          {/* Existing links */}
          {links.length === 0 ? (
            <p className="text-xs text-slate-400 mb-3">No social links yet</p>
          ) : (
            <div className="space-y-1.5 mb-4" data-testid="existing-social-links">
              {links.map((link, i) => (
                <div key={i} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-100" data-testid={`social-entry-${i}`}>
                  <PlatformIcon platform={link.platform} size={16} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-slate-600">{PLATFORM_CONFIG[link.platform]?.label || link.platform}</span>
                    <span className="text-[10px] text-slate-400 block truncate">{link.url}</span>
                  </div>
                  {link.source && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 flex-shrink-0">{link.source}</span>}
                  <button onClick={() => removeLink(link.platform, link.url)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0" data-testid={`remove-social-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new */}
          <div className="flex gap-2 items-end" data-testid="add-social-form">
            <div className="flex-shrink-0">
              <label className="text-[10px] font-semibold text-slate-500 block mb-1">Platform</label>
              <select
                value={newPlatform}
                onChange={(e) => setNewPlatform(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300"
                data-testid="social-platform-select"
              >
                <option value="">Select...</option>
                {PLATFORMS.map(p => (
                  <option key={p} value={p}>{PLATFORM_CONFIG[p]?.label || p}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-slate-500 block mb-1">URL</label>
              <input
                type="url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://..."
                className="w-full text-xs border border-slate-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                data-testid="social-url-input"
              />
            </div>
            <button
              onClick={addLink}
              disabled={adding || !newPlatform || !newUrl.trim()}
              className="px-3 py-2 rounded-lg bg-indigo-500 text-white text-xs font-semibold hover:bg-indigo-600 disabled:opacity-40 flex items-center gap-1 flex-shrink-0"
              data-testid="social-add-btn"
            >
              {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
